// Meal-catalog reader.
//
// Single Supabase-backed catalog source for the customer-facing meal
// planner.  Reads public.meal_planner_view via the canonical Supabase
// client — this is the authoritative catalog rebuilt by migration
// 0006.  No hardcoded fallback meals exist in this codebase, and this
// module is the only place any planner surface queries meals.
//
// Access model:
//   public.meal_planner_view is granted to `authenticated` only.  The
//   view is unreadable with the browser's anonymous (publishable) key.
//   Consumers must call useMealCatalog() from behind a session guard.
//
// Row identity is etm_id (permanent product identifier assigned in
// Supabase).  We never fabricate a client-side id.  We never mutate
// stored nutrition values; presentation-layer rounding is the
// component's responsibility.

import { useEffect, useMemo, useState } from 'react';
import { supabase, supabaseConfigured } from './supabase';

const VIEW_NAME = 'meal_planner_view';

// Customer-safe error message.  Database internals, SQL text, and
// Supabase error codes are logged (sanitized) but never surfaced.
const CUSTOMER_SAFE_MESSAGE =
  'We could not load the meal catalog. Please try again in a moment.';

function logSanitized(label, err) {
  try {
    console.warn('[etff:catalog]', label, {
      name: err?.name || null,
      code: err?.code || null,
      message: err?.message || null,
    });
  } catch {
    /* ignore */
  }
}

// Return the numeric value of the first field present on the row from
// a small set of expected column aliases, or null when none is set.
// This is a *read helper only* — it never invents a value.
function pickNumber(row, keys) {
  for (const k of keys) {
    const v = row?.[k];
    if (v === null || v === undefined || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function pickString(row, keys) {
  for (const k of keys) {
    const v = row?.[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

// Normalize a raw view row into a display-friendly shape.  All
// nutrition values are passed through unchanged from the database.
// etm_id and display_order are required by contract; if either is
// missing on a row, the row is dropped and a sanitized warning is
// logged (this prevents rendering orphan rows).
export function normalizeMealRow(row) {
  const etm_id = pickString(row, ['etm_id']);
  const display_order = pickNumber(row, ['display_order']);
  const name = pickString(row, ['name', 'product_name']);
  if (!etm_id || display_order === null || !name) {
    return null;
  }
  return {
    etm_id,
    display_order,
    name,
    category: pickString(row, ['category']),
    serving_size: pickString(row, [
      'serving_size',
      'serving_size_display',
      'serving',
    ]),
    calories: pickNumber(row, ['calories']),
    protein_g: pickNumber(row, ['protein_g', 'protein']),
    carbs_g: pickNumber(row, ['carbs_g', 'carbohydrates_g', 'carbohydrates']),
    fat_g: pickNumber(row, ['fat_g', 'fat']),
    fiber_g: pickNumber(row, ['fiber_g', 'fiber']),
    saturated_fat_g: pickNumber(row, ['saturated_fat_g', 'saturated_fat']),
    trans_fat_g: pickNumber(row, ['trans_fat_g', 'trans_fat']),
    cholesterol_mg: pickNumber(row, ['cholesterol_mg', 'cholesterol']),
    sodium_mg: pickNumber(row, ['sodium_mg', 'sodium']),
    sugars_g: pickNumber(row, ['sugars_g', 'sugars']),
    added_sugars_g: pickNumber(row, ['added_sugars_g', 'added_sugars']),
    vitamin_d_mcg: pickNumber(row, ['vitamin_d_mcg', 'vitamin_d']),
    calcium_mg: pickNumber(row, ['calcium_mg', 'calcium']),
    iron_mg: pickNumber(row, ['iron_mg', 'iron']),
    potassium_mg: pickNumber(row, ['potassium_mg', 'potassium']),
    allergens: Array.isArray(row?.allergens) ? row.allergens : null,
    // Raw row retained for debugging in dev tools only.  Not consumed
    // by the calculation layer.
    _raw: row,
  };
}

// Fetch the catalog exactly as the planner should consume it.
// Consumers wanting a promise-only shape can call this directly; most
// consumers should use useMealCatalog().
export async function fetchMealCatalog() {
  if (!supabaseConfigured || !supabase) {
    const err = new Error('supabase not configured');
    logSanitized('fetch:not_configured', err);
    return {
      ok: false,
      meals: [],
      error: CUSTOMER_SAFE_MESSAGE,
    };
  }
  const { data, error } = await supabase
    .from(VIEW_NAME)
    .select('*')
    .order('display_order', { ascending: true });
  if (error) {
    logSanitized('fetch:error', error);
    return { ok: false, meals: [], error: CUSTOMER_SAFE_MESSAGE };
  }
  const rows = Array.isArray(data) ? data : [];
  const meals = [];
  for (const r of rows) {
    const m = normalizeMealRow(r);
    if (m) meals.push(m);
    else logSanitized('fetch:dropped_row_missing_identity', {
      message: 'row missing etm_id, display_order, or name',
    });
  }
  return { ok: true, meals, error: null };
}

// React hook — loading / error / meals shape, plus a stable `refresh`.
export function useMealCatalog() {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMealCatalog().then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setMeals(r.meals);
        setError(null);
      } else {
        setMeals([]);
        setError(r.error);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const byCategory = useMemo(() => {
    const map = new Map();
    for (const m of meals) {
      const key = m.category || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return map;
  }, [meals]);

  return {
    meals,
    byCategory,
    loading,
    error,
    refresh: () => setNonce((n) => n + 1),
  };
}
